"use strict";
/*--------------------------------------------------------------------------------------------------------------
 *  Copyright (c) insite-gmbh. All rights reserved.
 *  Licensed under the Apache License, Version 2.0. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------------------------*/
const Subject_1 = require("rxjs/Subject");
const domain_1 = require("../domain");
//==================================================================================================================================
/**
 * This stores the Subscriber information and the subject to execute the callback on the subscriber
 * @class PlcSubscriber
 * @author insite-gmbh
 */
class ScriptEventProxy {
    /**
     *
     */
    constructor(_cleanup) {
        this._cleanup = _cleanup;
        this.Id = null;
        /**
        *
        *
        * @property Functionname
        * @type {string}
        */
        this.Functionname = null;
        this.LastError = null;
        if (_cleanup == null)
            throw "cleanup delegate is null!";
    }
    /**
     * You can call subscribe on this property to get informed for updates.
     *
     * @property ChangeEvent
     * @type {Observable<DataChangeEvent>}
     */
    get ChangeEvent() {
        if (this.Subject == null)
            this.Subject = new Subject_1.Subject();
        return this.Subject.asObservable();
    }
    /**
     * unregister the variable on inax
     */
    dispose() {
        if (this._cleanup != null) {
            this._cleanup(this);
        }
    }
}
//==================================================================================================================================
/**
 * This class handles the proxy generation for the plc signalR hub
 * @class PlcMappingPack
 * @author insite-gmbh
 */
class ScriptEventProxyFactory {
    /**
     * This holds the instance of the plc hub proxy.
     *
     * @property _hubProxy
     * @type {any}
     * @private
     */
    constructor(_hubProxy) {
        this._hubProxy = _hubProxy;
        /**
         * This holds the list of proxies for change events
         *
         * @property _proxies
         * @type {Array<PlcEventProxy>}
         * @private
         */
        this._proxies = new Array();
        if (_hubProxy == null)
            throw "scriptHubProxy is null!";
        this._hubProxy.on("scriptPropertiesChanged", (scriptUnit, propertyNames, propertyValues) => {
            let dce = new domain_1.PropertyChangeEvent();
            dce.ScriptUnit = scriptUnit;
            dce.Properties = propertyNames;
            dce.Values = propertyValues;
            this._proxies.forEach(channelSub => {
                if (channelSub.ScriptUnit === scriptUnit) {
                    channelSub.Subject.next(dce);
                }
            });
        });
        this._hubProxy.on("scriptPropertyChanged", (scriptUnit, propertyNames, propertyValue) => {
            let dce = new domain_1.PropertyChangeEvent();
            dce.ScriptUnit = scriptUnit;
            dce.Properties = [propertyNames];
            dce.Values = [propertyValue];
            this._proxies.forEach(channelSub => {
                if (channelSub.ScriptUnit === scriptUnit) {
                    channelSub.Subject.next(dce);
                }
            });
        });
        this._hubProxy.on("scriptsInitialized", () => {
        });
    }
    create(scriptUnit, functionName = null) {
        // Now we just create our internal object so we can track this subject
        // in case someone else wants it too
        let channelSub = new ScriptEventProxy((sub) => this.release(sub));
        if (functionName != null) {
            channelSub.Id = scriptUnit + functionName + Date.now().toLocaleString();
            channelSub.Functionname = functionName;
        }
        else {
            channelSub.Id = scriptUnit + Date.now().toLocaleString();
        }
        channelSub.ScriptUnit = scriptUnit;
        channelSub.Subject = new Subject_1.Subject();
        this._proxies.push(channelSub);
        return channelSub;
    }
    setProperty(scriptUnit, propertyName, value) {
        let subject = new Subject_1.Subject();
        this._hubProxy.invoke("SetScriptProperty", scriptUnit, propertyName, value)
            .done(() => {
            subject.next(true);
            subject.complete();
        })
            .fail((error) => {
            subject.next(false);
            subject.error(error);
        });
        return subject.asObservable();
    }
    invoke(scriptUnit, functionName, parameters, withCallback = false) {
        let evProxy = withCallback ? this.create(scriptUnit, functionName) : null;
        let result = new Subject_1.Subject();
        let observable = result.asObservable();
        let callerInfo = {
            ScriptUnit: scriptUnit,
            FunctionName: functionName,
            Parameters: parameters
        };
        if (evProxy != null) {
            evProxy.ChangeEvent.subscribe((event) => {
                let res = new domain_1.MethodPropertyChangeEvent();
                res.CallerInfo = callerInfo;
                res.Event = event;
                result.next(res);
            });
        }
        this._hubProxy.invoke("RunScript", scriptUnit, functionName, parameters)
            .done(() => {
            if (evProxy != null)
                evProxy.dispose();
            let res = new domain_1.MethodPropertyChangeEvent();
            res.CallerInfo = callerInfo;
            res.IsFinished = true;
            result.next(res);
            result.complete();
        })
            .fail((error) => {
            if (evProxy != null) {
                evProxy.LastError = error;
                evProxy.dispose();
            }
            let res = new domain_1.MethodPropertyChangeEvent();
            res.CallerInfo = callerInfo;
            res.IsFinished = true;
            res.Error = error;
            result.next(res);
            result.error(error);
        });
        return observable;
    }
    release(proxy) {
        var channelSub = proxy;
        if (channelSub.LastError == null)
            channelSub.Subject.complete();
        else
            channelSub.Subject.error(channelSub.LastError);
        let idx = this._proxies.indexOf(channelSub);
        if (idx != -1)
            this._proxies.splice(idx, 1);
        else {
            console.log(`Subscriber for scriptunit ${proxy.ScriptUnit} not found after unsubscribe!`);
        }
    }
}
exports.ScriptEventProxyFactory = ScriptEventProxyFactory;
